import { useMemo, useState, useCallback } from "react";
import TableContainer from "../../../common/table/TableContainer";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import load from "../../../assets/images/load-img.png";
import {
  getPaymentByBusinessId,
  getPaymentByMerchandId,
} from "../services/paymentApi";
import { useQuery } from "react-query";
import "../utils/paiement.css";
import { FormatCurrency } from "../../../common/format/formatCurrency";
import FormatDate from "../../../common/format/formatDate";
import DetailRecuVersement from "../../versement/component/historique/detailRecu";
import { DatePicker, Select } from "antd";
import { RiFileExcel2Line } from "react-icons/ri";
import * as XLSX from "xlsx";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

const STATUS_FILTER_OPTIONS = [
  { value: "ALL", label: "Tous les statuts" },
  { value: "SUCCESS", label: "Réussie" },
  { value: "REJECTED", label: "Échouée" },
];


const STATUS_CONFIG = {
  SUCCESS: {
    backgroundColor: "#EBFFEA",
    textColor: "#349C32",
    label: "Réussie",
    icon: (color) => <CheckOutlined style={{ color, marginRight: 6 }} />,
  },
  PENDING: {
    backgroundColor: "#F6F6F6",
    textColor: "#FF9F00",
    label: " En attente",
    icon: () => <img src={load} alt="En attente" />,
  },
  DEFAULT: {
    backgroundColor: "#FFF2F2",
    textColor: "#E00909",
    label: "Échec",
    icon: (color) => <CloseOutlined style={{ color, marginRight: 6 }} />,
  },
};

const EXCEL_COLUMN_WIDTHS = [
  { wch: 20 }, // Business
  { wch: 20 }, // Marchand
  { wch: 15 }, // Montant réel
  { wch: 20 }, // Mode de paiement
  { wch: 20 }, // Numéro de client
  { wch: 20 }, // Nom du client
  { wch: 12 }, // Statut
  { wch: 35 }, // Référence de transaction
  { wch: 22 }, // Date de paiement
];

const mapPaymentsForExport = (data) =>
  data.map((p) => ({
    Business: p.developerAccountName || "",
    Marchand: p.merchantAccountName || "",
    "Montant réel": p.realAmount || "",
    "Mode de paiement": p.paymentMethodName || "",
    "Numéro de client": p.clientNumber || "",
    "Nom du client": p.clientFullName || "",
    Statut: p.status || "",
    "Référence de transaction": p.transactionReference || "",
    "Date de paiement": p.createdAt
      ? dayjs(p.createdAt).format("DD/MM/YYYY HH:mm")
      : "",
  }));

const downloadBlob = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const buildExportFileName = (dateRange) => {
  if (dateRange?.[0] && dateRange?.[1]) {
    return `paiements_${dateRange[0].format("YYYY-MM-DD")}_au_${dateRange[1].format("YYYY-MM-DD")}.xlsx`;
  }
  return `paiements_${dayjs().format("YYYY-MM-DD_HHmmss")}.xlsx`;
};

const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.DEFAULT;
  return (
    <span
      style={{
        backgroundColor: config.backgroundColor,
        color: config.textColor,
        padding: "4px 10px",
        borderRadius: "20px",
        display: "inline-flex",
        alignItems: "center",
        fontWeight: 500,
        fontSize: 13,
      }}
    >
      {config.icon(config.textColor)}
      {config.label}
    </span>
  );
};

const PaymentsHistorySolde = ({ busId }) => {
  const currentUser = JSON.parse(sessionStorage.getItem("authUser"));
  const merchandId = currentUser?.user?.id;
  const role = currentUser?.roles;
  const permissions = currentUser?.user?.permissions;
  const isAuthorized =
    role === "COLLABORATOR" ? permissions?.includes("VIEW_TRANSACTIONS") : true;

  const [selectedPayment, setSelectedPayment] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  const [statusFilter, setStatusFilter] = useState("ALL");

  const [isExporting, setIsExporting] = useState(false);

  const props = useMemo(
    () => ({
      busId,
      token: currentUser?.token,
      merchandId,
    }),
    [busId, currentUser?.token, merchandId],
  );

  const { data, isLoading, isError } = useQuery(
    ["payments", busId],
    () =>
      busId === "all"
        ? getPaymentByMerchandId(props)
        : getPaymentByBusinessId(props),
    {
      enabled: !!merchandId || !!busId,
    },
  );

  const payments = useMemo(
    () => (Array.isArray(data?.data) ? data.data : []),
    [data],
  );

  const filteredPayments = useMemo(() => {
    const start = dateRange?.[0]?.startOf("day");
    const end = dateRange?.[1]?.endOf("day");

    return payments.filter((p) => {
      if (start && end) {
        const paymentDate = dayjs(p.createdAt);
        const withinRange =
          (paymentDate.isAfter(start) || paymentDate.isSame(start)) &&
          (paymentDate.isBefore(end) || paymentDate.isSame(end));
        if (!withinRange) return false;
      }

      if (statusFilter !== "ALL" && p.status !== statusFilter) return false;

      return true;
    });
  }, [payments, dateRange, statusFilter]);

  const handleConsulter = useCallback((payment) => {
    setSelectedPayment(payment);
    setIsModalOpen(true);
  }, []);

  const columns = useMemo(
    () => [
      {
        Header: "Business",
        accessor: "developerAccountName",
        filterable: false,
      },
      {
        Header: "Compte",
        accessor: "clientNumber",
        Cell: ({ row }) => {
          const { clientNumber, countryCode } = row.original;
          const formattedPhoneNumber =
            clientNumber && countryCode
              ? parsePhoneNumberFromString(clientNumber, countryCode)
              : null;
          return formattedPhoneNumber
            ? formattedPhoneNumber.formatInternational()
            : "Non défini";
        },
        filterable: false,
      },
      {
        Header: "Montant reçu",
        accessor: "realAmount",
        Cell: ({ row }) => (
          <span>
            {FormatCurrency(
              parseInt(row.original.realAmount, 10),
              row.original.currency,
            )}
          </span>
        ),
        filterable: false,
      },
      {
        Header: "Date de paiement",
        accessor: "createdAt",
        Cell: ({ value }) => <FormatDate date={value} showTime />,
        filterable: false,
      },
      {
        Header: "Statut",
        accessor: "status",
        Cell: ({ value }) => <StatusBadge status={value} />,
        filterable: false,
      },
      {
        Header: "Client",
        accessor: "clientFullName",
        Cell: ({ value }) => <span style={value ? {} : { color: "#ccc", fontSize: 10 } } >{value ? value : "Aucun nom renseigné."}</span>,
        filterable: false,
      },
      {
        Header: "Action",
        Cell: ({ row }) => (
          <button
            className="btn-consulter"
            onClick={() => handleConsulter(row.original)}
          >
            Consulter
          </button>
        ),
        filterable: false,
      },
    ],
    [handleConsulter],
  );

  const exportExcel = useCallback(
    (dataToExport) => {
      if (!dataToExport?.length) return;

      const formattedData = mapPaymentsForExport(dataToExport);
      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      worksheet["!cols"] = EXCEL_COLUMN_WIDTHS;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Paiements");

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      const blob = new Blob([excelBuffer], {
        type: "application/octet-stream",
      });

      downloadBlob(blob, buildExportFileName(dateRange));
    },
    [dateRange],
  );

   const handleExportExcel = useCallback(async () => {
    if (isExporting) return;
    setIsExporting(true);
    await new Promise((resolve) => setTimeout(resolve, 50));
    try {
      exportExcel(filteredPayments);
    } finally {
      setIsExporting(false);
    }
  }, [exportExcel, filteredPayments, isExporting]);

  const handleCloseModal = useCallback(() => setIsModalOpen(false), []);

  return (
    <div className="page-content">
      <div className="d-flex justify-content-between align-items-center">
        <h2 className="title-page-country mt-2 mb-3 justify-content-between">
          Historique de mes paiements
        </h2>

        <div className="d-flex flex-wrap gap-2 align-items-center">
          <RangePicker
            onChange={setDateRange}
            allowClear
            format="DD/MM/YYYY"
            placeholder={["Date début", "Date fin"]}
          />

          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            options={STATUS_FILTER_OPTIONS}
            style={{ minWidth: 160 }}
          />

          <button
            onClick={handleExportExcel}
            disabled={filteredPayments.length === 0 || isExporting}
            className="d-flex gap-2 align-items-center"
            style={{
              background: "#325DFF",
              color: "#FFF",
              borderRadius: 5,
              padding: "4px 15px",
              opacity: filteredPayments.length === 0 ? 0.6 : 1,
              cursor:
                filteredPayments.length === 0 || isExporting
                  ? "not-allowed"
                  : "pointer",
            }}
          >
            {isExporting ? (
              <>
                <span className="spinner-export" />
                Export en cours...
              </>
            ) : (
              <>
                <RiFileExcel2Line />
                Exporter
              </>
            )}
          </button>
        </div>
      </div>

      <TableContainer
        columns={columns}
        data={filteredPayments}
        isGlobalSearch={true}
        isGlobalFilter={true}
        customPageSize={5}
        className="custom-header-css"
        divClass="table-responsive table-card mb-3"
        tableClass="align-middle table-nowrap"
        theadClass="table-light"
        SearchPlaceholder="Recherche..."
        isLoading={isLoading}
        isError={!isAuthorized || isError}
        error={
          !isAuthorized
            ? "Pas autorisé à consulter cette liste."
            : "Erreur, contactez le support"
        }
      />

      <DetailRecuVersement
        isOpen={isModalOpen}
        toggle={handleCloseModal}
        selectedVersement={selectedPayment}
      />
    </div>
  );
};

export default PaymentsHistorySolde;